"use client";
import { useEffect, useState } from "react";

/**
 * Inline preview for the Keystatic "Image" block (rendered in the editor only).
 * Shows the actual image scaled to the chosen render size plus size/caption, so a
 * pasted block looks like the image — not a bare placeholder card.
 *
 * Lives in its own "use client" module because `keystatic.config.tsx` is imported
 * by the server (the site reader) too; React hooks can't live in that shared
 * module, so the hook-using preview is isolated here.
 *
 * While editing, the image field value is `{ data, extension, filename }`; we turn
 * its bytes into a temporary object URL (revoked on unmount / image change).
 */
export default function ImageBlockPreview(props: {
  value: {
    src: { data: Uint8Array; extension: string; filename: string } | null;
    alt: string;
    caption: string;
    size: string;
  };
}) {
  const { src, alt, caption, size } = props.value;
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const data = src?.data;
    if (!data) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(new Blob([data as unknown as BlobPart]));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [src?.filename, src?.extension]);

  const maxWidth =
    size === "small" ? 160 : size === "medium" ? 260 : size === "large" ? 380 : 520;

  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: 10,
        padding: 10,
        background: "rgba(0,0,0,0.015)",
      }}
    >
      {url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={url}
          alt={alt || ""}
          style={{
            maxWidth,
            width: "100%",
            height: "auto",
            display: "block",
            borderRadius: 6,
          }}
        />
      ) : (
        <div style={{ fontSize: 13, color: "#888" }}>🖼 No image selected</div>
      )}
      <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
        Size: <strong>{size}</strong>
        {caption ? ` · ${caption}` : ""}
      </div>
    </div>
  );
}
